import { createApi } from '@reduxjs/toolkit/query/react';
import baseQuery from '../../Shared/API/config';

export interface Strand {
  id: number;
  displayName: string;
  subject?: {
    id: number;
    displayName: string;
  };
  curriculum?: {
    id: number;
    displayName: string;
  };
  grade?: number;
  gradeDisplay?: string;
  order: number;
  subStrandsCount?: number;
  created: string;
  modified: string;
}

export interface Substrand {
  id: number;
  displayName: string;
  strand: {
    id: number;
    displayName: string;
  };
  order: number;
  learningIndicatorsCount: number;
  created: string;
  modified: string;
}

export interface LearningIndicator {
  id: number;
  displayName: string;
  code?: string;
  description?: string;
  substrand?: {
    id: number;
    displayName: string;
  };
  strandId?: number;
  substrandId?: number;
  order?: number;
  created: string;
  modified: string;
}

export interface SubjectOutline {
  id: number;
  sessionId: number;
  subjectId: number;
  subStrandId: number;
  classroomId: number;
  schoolId: number;
  lessonsCount: number;
  created: string;
  modified: string;
  // Expanded objects from API response
  strand?: {
    id: number;
    name: string;
  };
  substrand?: {
    id: number;
    name: string;
  };
  subject?: {
    id: number;
    name: string;
  };
  session?: {
    id: number;
    name: string;
  };
  classroom?: {
    id: number;
    name: string;
  };
}

export interface CreateStrandRequest {
  displayName: string;
  subjectId?: number;
  curriculumId?: number;
  grade?: number;
  order?: number;
}

export interface CreateSubstrandRequest {
  displayName: string;
  strandId: number; // Now required
  subjectId?: number;
}

export interface CreateLearningIndicatorRequest {
  displayName: string;
  code?: string;
  description?: string;
  substrandId: number;
}

export const OutlineApi = createApi({
  reducerPath: 'OutlineApi',
  baseQuery,
  tagTypes: ['Strand', 'SubStrand', 'LearningIndicator', 'SubjectOutline'],
  endpoints: builder => ({
    // Subject Outline endpoints
    getSubjectOutlines: builder.query<
      { data: SubjectOutline[]; meta: any },
      {
        sessionId?: number;
        subjectId?: number;
        classroomId?: number;
        page?: number;
        pageSize?: number;
        search?: string;
      }
    >({
      query: params => {
        const searchParams = new URLSearchParams();
        Object.entries(params).forEach(([key, value]) => {
          if (value !== undefined) {
            searchParams.append(key, String(value));
          }
        });
        const queryString = searchParams.toString();
        return `/academics/subject-outlines${queryString ? `?${queryString}` : ''}`;
      },
      providesTags: ['SubjectOutline'],
    }),

    createSubjectOutline: builder.mutation<
      { data: SubjectOutline; meta: any },
      { sessionId: number; subjectId: number; subStrandId: number; classroomId: number }
    >({
      query: body => ({
        url: '/academics/subject-outlines',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['SubjectOutline'],
    }),

    updateSubjectOutline: builder.mutation<
      { data: SubjectOutline; meta: any },
      {
        id: number;
        updates: Partial<Omit<SubjectOutline, 'id' | 'created' | 'modified'>>;
      }
    >({
      query: ({ id, updates }) => ({
        url: `/academics/subject-outlines/${id}`,
        method: 'PATCH',
        body: updates,
      }),
      invalidatesTags: ['SubjectOutline'],
    }),

    deleteSubjectOutline: builder.mutation<void, { id: number }>({
      query: ({ id }) => ({
        url: `/academics/subject-outlines/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['SubjectOutline'],
    }),

    // Strand endpoints
    getStrands: builder.query<
      { data: Strand[]; meta: any },
      {
        page?: string;
        pageSize?: string;
        search?: string;
        subjectId?: string;
      }
    >({
      query: (params) => {
        const searchParams = new URLSearchParams();
        Object.entries(params).forEach(([key, value]) => {
          if (value !== undefined) {
            searchParams.append(key, String(value));
          }
        });
        const queryString = searchParams.toString();
        return `/academics/strands${queryString ? `?${queryString}` : ''}`;
      },
      providesTags: ['Strand'],
    }),

    createStrand: builder.mutation<
      { data: Strand; meta: any },
      CreateStrandRequest
    >({
      query: body => ({
        url: '/academics/strands',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Strand'],
    }),

    getStrand: builder.query<{ data: Strand; meta: any }, { id: number }>({
      query: ({ id }) => `/academics/strands/${id}`,
      providesTags: ['Strand'],
    }),

    updateStrand: builder.mutation<
      { data: Strand; meta: any },
      { id: number; updates: Partial<CreateStrandRequest> }
    >({
      query: ({ id, updates }) => ({
        url: `/academics/strands/${id}`,
        method: 'PATCH',
        body: updates,
      }),
      invalidatesTags: ['Strand'],
    }),

    deleteStrand: builder.mutation<void, { id: number }>({
      query: ({ id }) => ({
        url: `/academics/strands/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Strand'],
    }),

    // Substrand endpoints
    getSubStrands: builder.query<
      { data: Substrand[]; meta: any },
      {
        page?: string;
        pageSize?: string;
        search?: string;
        strandId?: string;
        subjectId?: string;
      }
    >({
      query: params => {
        const searchParams = new URLSearchParams();
        Object.entries(params).forEach(([key, value]) => {
          if (value !== undefined) {
            searchParams.append(key, String(value));
          }
        });
        const queryString = searchParams.toString();
        return `/academics/sub-strands${queryString ? `?${queryString}` : ''}`;
      },
      providesTags: ['SubStrand'],
    }),

    createSubStrand: builder.mutation<
      { data: Substrand; meta: any },
      CreateSubstrandRequest
    >({
      query: body => ({
        url: '/academics/sub-strands',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['SubStrand'],
    }),

    getSubStrand: builder.query<{ data: Substrand; meta: any }, { id: number }>(
      {
        query: ({ id }) => `/academics/sub-strands/${id}`,
        providesTags: ['SubStrand'],
      }
    ),

    updateSubStrand: builder.mutation<
      { data: Substrand; meta: any },
      { id: number; updates: Partial<CreateSubstrandRequest> }
    >({
      query: ({ id, updates }) => ({
        url: `/academics/sub-strands/${id}`,
        method: 'PATCH',
        body: updates,
      }),
      invalidatesTags: ['SubStrand'],
    }),

    deleteSubStrand: builder.mutation<void, { id: number }>({
      query: ({ id }) => ({
        url: `/academics/sub-strands/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['SubStrand'],
    }),

    // Learning Indicator endpoints
    getLearningIndicators: builder.query<
      { data: LearningIndicator[]; meta: any },
      {
        page?: number;
        pageSize?: number;
        search?: string;
        strandId?: number;
        substrandId?: number;
        subjectOutlineId?: number;
      }
    >({
      query: params => {
        const searchParams = new URLSearchParams();
        Object.entries(params).forEach(([key, value]) => {
          if (value !== undefined) {
            searchParams.append(key, String(value));
          }
        });
        const queryString = searchParams.toString();
        return `/academics/learning-indicators${queryString ? `?${queryString}` : ''}`;
      },
      providesTags: ['LearningIndicator'],
    }),

    createLearningIndicator: builder.mutation<
      { data: LearningIndicator; meta: any },
      CreateLearningIndicatorRequest
    >({
      query: body => ({
        url: '/academics/learning-indicators',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['LearningIndicator'],
    }),

    getLearningIndicator: builder.query<
      { data: LearningIndicator; meta: any },
      { id: number }
    >({
      query: ({ id }) => `/academics/learning-indicators/${id}`,
      providesTags: ['LearningIndicator'],
    }),

    updateLearningIndicator: builder.mutation<
      { data: LearningIndicator; meta: any },
      { id: number; updates: Partial<CreateLearningIndicatorRequest> }
    >({
      query: ({ id, updates }) => ({
        url: `/academics/learning-indicators/${id}`,
        method: 'PATCH',
        body: updates,
      }),
      invalidatesTags: ['LearningIndicator'],
    }),

    deleteLearningIndicator: builder.mutation<void, { id: number }>({
      query: ({ id }) => ({
        url: `/academics/learning-indicators/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['LearningIndicator'],
    }),
  }),
});

export const {
  // Subject Outlines
  useGetSubjectOutlinesQuery,
  useCreateSubjectOutlineMutation,
  useUpdateSubjectOutlineMutation,
  useDeleteSubjectOutlineMutation,
  // Strands
  useGetStrandsQuery,
  useGetStrandQuery,
  useCreateStrandMutation,
  useUpdateStrandMutation,
  useDeleteStrandMutation,
  // Substrands
  useGetSubStrandsQuery,
  useGetSubStrandQuery,
  useCreateSubStrandMutation,
  useUpdateSubStrandMutation,
  useDeleteSubStrandMutation,
  // Learning Indicators
  useGetLearningIndicatorsQuery,
  useGetLearningIndicatorQuery,
  useCreateLearningIndicatorMutation,
  useUpdateLearningIndicatorMutation,
  useDeleteLearningIndicatorMutation,
} = OutlineApi;
